import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Check, X, ImagePlus, Loader2 } from "lucide-react";
import api from "@/api";

const inputCls = "rounded-lg border border-clayline bg-cream px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-terracotta/60";
const emptyForm = { title: "", slug: "", excerpt: "", tag: "Story", date: "", cover: "", content: "" };
const imgSrc = (p) => (!p ? "" : (p.startsWith("http") || p.startsWith("/")) ? p : `${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`);

const JournalManager = () => {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () =>
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => toast.error("Failed to load stories"));

  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startEdit = async (p) => {
    try {
      const { data } = await api.get(`/posts/${p.slug}`);
      setEditing(p.id);
      setAdding(false);
      setForm({
        title: data.title, slug: data.slug, excerpt: data.excerpt || "",
        tag: data.tag || "Story", date: data.date || "", cover: data.cover || "",
        content: data.content || "",
      });
    } catch {
      toast.error("Could not load the story");
    }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, cover: data.path }));
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Try a smaller image.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and story content are required");
      return;
    }
    try {
      if (adding) await api.post("/admin/posts", form);
      else await api.put(`/admin/posts/${editing}`, form);
      toast.success(adding ? "Story published" : "Story updated");
      setEditing(null);
      setAdding(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed. Please try again.");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/posts/${p.id}`);
      toast.success("Story deleted");
      load();
    } catch {
      toast.error("Delete failed. Please try again.");
    }
  };

  const cancel = () => {
    setEditing(null);
    setAdding(false);
    setForm(emptyForm);
  };

  const editForm = (
    <div className="bg-sand/60 border border-terracotta/50 rounded-xl p-4 space-y-3" data-testid="journal-edit-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <input placeholder="Story title" value={form.title} onChange={set("title")} className={inputCls} data-testid="journal-form-title" />
        <input placeholder="Slug (auto from title if blank)" value={form.slug} onChange={set("slug")} className={inputCls} data-testid="journal-form-slug" />
        <select value={form.tag} onChange={set("tag")} className={inputCls} data-testid="journal-form-tag">
          {["Story", "Craft", "Brand", "Recipe", "News"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input type="date" value={form.date} onChange={set("date")} className={inputCls} data-testid="journal-form-date" />
      </div>
      <input placeholder="Excerpt (shown on the journal list)" value={form.excerpt} onChange={set("excerpt")} className={inputCls} data-testid="journal-form-excerpt" />
      <textarea rows={8} placeholder="Story content — blank line between paragraphs…" value={form.content} onChange={set("content")} className={inputCls} data-testid="journal-form-content" />
      <div className="flex items-center gap-4 flex-wrap">
        {form.cover ? (
          <div className="flex items-center gap-3">
            <img src={imgSrc(form.cover)} alt="Cover" className="w-20 h-14 rounded-xl object-cover border border-clayline" data-testid="journal-form-cover-preview" />
            <button type="button" onClick={() => setForm({ ...form, cover: "" })} data-testid="journal-form-cover-remove" className="font-mono text-[11px] uppercase tracking-wider text-red-600 hover:underline">
              Remove
            </button>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] border border-dashed border-terracotta text-terracotta cursor-pointer hover:bg-terracotta hover:text-cream transition-colors" data-testid="journal-form-cover-upload">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
            {uploading ? "Uploading…" : "Cover Photo"}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadCover} />
          </label>
        )}
        <div className="flex gap-3 ml-auto">
          <button onClick={save} data-testid="journal-save-btn" className="rounded-full px-6 py-2.5 font-mono text-xs uppercase tracking-[0.15em] bg-terracotta text-cream hover:bg-charcoal transition-colors inline-flex items-center gap-2">
            <Check size={14} /> {adding ? "Publish" : "Save"}
          </button>
          <button onClick={cancel} data-testid="journal-cancel-btn" className="rounded-full px-6 py-2.5 font-mono text-xs uppercase tracking-[0.15em] border border-clayline hover:border-charcoal transition-colors inline-flex items-center gap-2">
            <X size={14} /> Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="journal-manager">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-charcoal/50">{posts.length} stories</p>
        <button
          onClick={() => { setAdding(true); setEditing(null); setForm(emptyForm); }}
          data-testid="journal-add-btn"
          className="rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] bg-charcoal text-cream hover:bg-terracotta transition-colors inline-flex items-center gap-2"
        >
          <Plus size={14} /> New Story
        </button>
      </div>
      {adding && <div className="mb-6">{editForm}</div>}
      <div className="space-y-4">
        {posts.map((p) => (
          editing === p.id ? (
            <div key={p.id}>{editForm}</div>
          ) : (
            <div key={p.id} className="bg-cream border border-clayline rounded-2xl p-5 flex items-start gap-4 group" data-testid={`journal-row-${p.slug}`}>
              {p.cover ? (
                <img src={imgSrc(p.cover)} alt={p.title} className="w-20 h-14 rounded-xl object-cover border border-clayline shrink-0" />
              ) : (
                <span className="w-20 h-14 rounded-xl border border-dashed border-clayline shrink-0 flex items-center justify-center text-charcoal/25"><ImagePlus size={18} /></span>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-serif font-bold text-lg">{p.title}</h3>
                  <span className="font-mono text-[10px] uppercase tracking-wider bg-sand text-charcoal px-2.5 py-0.5 rounded-full">{p.tag}</span>
                </div>
                <p className="text-xs text-charcoal/55 mt-1 truncate">{p.excerpt}</p>
                <p className="font-mono text-[10px] text-charcoal/40 mt-1.5">/journal/{p.slug} · {p.date}</p>
              </div>
              <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
                <button onClick={() => startEdit(p)} data-testid={`journal-edit-${p.slug}`} className="p-2 hover:text-terracotta transition-colors" aria-label={`Edit ${p.title}`}>
                  <Pencil size={15} />
                </button>
                <button onClick={() => remove(p)} data-testid={`journal-delete-${p.slug}`} className="p-2 hover:text-red-600 transition-colors" aria-label={`Delete ${p.title}`}>
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
};

export default JournalManager;
